
import React, { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Plus, X, Image, GripVertical } from 'lucide-react';
import { optimizeCloudinaryUrl, getThumbnailImageUrl } from '@/lib/cloudinary';

interface PropertyImagesFormProps {
  images: string[];
  onImagesChange: (images: string[]) => void;
}

const PropertyImagesForm = ({ images, onImagesChange }: PropertyImagesFormProps) => {
  const [newImageUrl, setNewImageUrl] = useState('');
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  const addImage = () => {
    if (newImageUrl.trim() && !images.includes(newImageUrl.trim())) {
      const optimizedUrl = optimizeCloudinaryUrl(newImageUrl.trim());
      onImagesChange([...images, optimizedUrl]);
      setNewImageUrl('');
    }
  };

  const removeImage = (index: number) => {
    const updatedImages = images.filter((_, i) => i !== index);
    onImagesChange(updatedImages);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addImage();
    }
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    const newImages = [...images];
    const draggedImage = newImages[draggedIndex];
    
    // Remove the dragged item
    newImages.splice(draggedIndex, 1);
    
    // Insert at the new position
    newImages.splice(dropIndex, 0, draggedImage);
    
    onImagesChange(newImages);
    setDraggedIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  return (
    <div className="space-y-4">
      <div>
        <Label>Imágenes</Label>
        <p className="text-sm text-muted-foreground mb-2">
          Agrega URLs de Cloudinary para las imágenes de la propiedad
        </p>
      </div>

      {/* Add new image URL */}
      <div className="flex gap-2">
        <Input
          placeholder="https://res.cloudinary.com/..."
          value={newImageUrl}
          onChange={(e) => setNewImageUrl(e.target.value)}
          onKeyPress={handleKeyPress}
        />
        <Button
          type="button"
          onClick={addImage}
          disabled={!newImageUrl.trim()}
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>

      {/* Images grid */}
      {images.length > 0 && (
        <div className="space-y-2">
          <Label className="text-sm font-medium">
            Imágenes agregadas ({images.length})
          </Label>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {images.map((imageUrl, index) => (
              <div
                key={imageUrl}
                className={`relative group rounded-lg overflow-hidden border-2 transition-all cursor-move ${
                  draggedIndex === index 
                    ? 'border-primary opacity-50' 
                    : 'border-transparent hover:border-primary/50'
                }`}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={handleDragEnd}
              >
                {/* Drag indicator */}
                <div className="absolute top-2 left-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="bg-background/80 backdrop-blur-sm rounded p-1">
                    <GripVertical className="h-4 w-4 text-muted-foreground" />
                  </div>
                </div>

                {/* Remove button */}
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={() => removeImage(index)}
                  className="absolute top-2 right-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8 p-0"
                >
                  <X className="h-4 w-4" />
                </Button>

                {/* Image */}
                <div className="aspect-square bg-muted">
                  <img
                    src={getThumbnailImageUrl(imageUrl)}
                    alt={`Imagen ${index + 1}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const parent = target.parentElement;
                      if (parent) {
                        parent.innerHTML = '<div class="w-full h-full flex items-center justify-center bg-muted"><svg class="w-8 h-8 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg></div>';
                      }
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {images.length === 0 && (
        <div className="text-center py-8 border-2 border-dashed border-muted rounded-lg">
          <Image className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No se han agregado imágenes aún</p>
          <p className="text-sm text-muted-foreground">
            Agrega las URLs de Cloudinary usando el campo de arriba
          </p>
        </div>
      )}
    </div>
  );
};

export default PropertyImagesForm;
